/**
 * @semo/api-contracts - Enum Options
 *
 * Frontend-facing enum options derived from backend enums.
 * Used for UI rendering in form dropdowns and selections.
 *
 * Source of Truth: Backend enum definitions (backend/src/quote-requests/domain/enums/)
 * Sync Mechanism: Manual (must be kept in sync with backend enums)
 */

// ============================================================================
// Construction Type Options (공사 유형) - 29 types
// ============================================================================

export interface EnumOption {
  value: string
  label: string
}

// ============================================================================
// Category Type Options (카테고리 유형)
// ============================================================================

export const CATEGORY_TYPE_OPTIONS: EnumOption[] = [
  { value: 'product', label: '물품구매' },
  { value: 'construction', label: '공사' },
  { value: 'manufacturing', label: '제조・용역' },
  { value: 'used', label: '중고 판매' },
]

export type CategoryType = 'product' | 'construction' | 'manufacturing' | 'used'

export const CONSTRUCTION_TYPE_OPTIONS: EnumOption[] = [
  { value: 'facility_maintenance', label: '시설물유지관리' },
  { value: 'interior_architecture', label: '실내건축' },
  { value: 'waterproofing_plaster_painting', label: '습식방수/미장/도장' },
  { value: 'mechanical_equipment', label: '기계설비' },
  { value: 'metal_structure_window', label: '금속구조물.창호' },
  { value: 'paving', label: '포장공사' },
  { value: 'electrical', label: '전기공사' },
  { value: 'scaffolding_demolition', label: '비계.구조물해체' },
  { value: 'asbestos_removal', label: '석면해체' },
  { value: 'rebar_concrete', label: '철근.콘크리트' },
  { value: 'fire_safety', label: '소방공사' },
  { value: 'landscaping', label: '조경' },
  { value: 'roofing_assembly', label: '지붕판금.건축물조립' },
  { value: 'elevator', label: '승강기' },
  { value: 'gas_facility', label: '가스시설' },
  { value: 'hvac', label: '냉.난방공사' },
  { value: 'telecommunications', label: '정보통신' },
  { value: 'cctv', label: 'CCTV공사' },
  { value: '365_corner', label: '365코너' },
  { value: 'civil_construction', label: '토목건축공사업' },
  { value: 'building_construction', label: '건축공사업' },
  { value: 'specialized_construction', label: '전문건설업' },
  { value: 'water_sewage', label: '상하수도설비' },
  { value: 'stone_work', label: '석공사' },
  { value: 'earthwork', label: '토공사' },
  { value: 'grouting', label: '그라우팅' },
  { value: 'dredging', label: '준설공사업' },
  { value: 'outdoor_signage', label: '옥외광고/간판' },
  { value: 'other', label: '기타' },
]

// ============================================================================
// Space Type Options (공간 종류) - Conditional: Only shown for specific construction types
// ============================================================================

export const SPACE_TYPE_OPTIONS: EnumOption[] = [
  { value: 'office', label: '사무실' },
  { value: 'retail', label: '매장' },
  { value: 'factory', label: '공장' },
  { value: 'warehouse', label: '창고' },
]

/**
 * Construction types that require space type selection
 * Used for conditional visibility in Frontend
 */
export const SPACE_TYPE_DEPENDS_ON = ['cctv', 'telecommunications', 'elevator']

// ============================================================================
// Construction Area Options (시공 평수) - Conditional: Only shown for specific construction types
// ============================================================================

export const CONSTRUCTION_AREA_OPTIONS: EnumOption[] = [
  { value: '30', label: '30평' },
  { value: '50', label: '50평' },
  { value: '100', label: '100평 이하' },
  { value: '200', label: '200평 이상' },
]

/**
 * Construction types that require construction area selection
 * Used for conditional visibility in Frontend
 */
export const CONSTRUCTION_AREA_DEPENDS_ON = [
  'interior_architecture',
  'facility_maintenance',
  'rebar_concrete',
]

// ============================================================================
// Additional Conditions Options (물품 구매 카테고리) - Product category
// ============================================================================

export const ADDITIONAL_CONDITIONS_PRODUCT: EnumOption[] = [
  { value: 'brand_agnostic', label: '브랜드 무관' },
  { value: 'equivalent_or_higher', label: '동등 이상' },
  { value: 'split_delivery', label: '분할 납품' },
  { value: 'packaging', label: '포장' },
  { value: 'installation', label: '설치' },
  { value: 'sample', label: '시료' },
  { value: 'certificate', label: '성적서' },
  { value: 'inventory_check', label: '재고 확인' },
]

// ============================================================================
// Additional Requests Options (공사 카테고리) - Construction category
// ============================================================================

export const ADDITIONAL_REQUESTS_CONSTRUCTION: EnumOption[] = [
  { value: 'night_work', label: '야간작업 가능' },
  { value: 'waste_collection', label: '폐기물 수거' },
  { value: 'material_included', label: '자재 포함' },
  { value: 'pre_visit', label: '사전 방문' },
  { value: 'site_coordination', label: '현장 조율' },
  { value: 'progress_payment', label: '기성 지급' },
  { value: 'construction_insurance', label: '공사보험' },
]

// ============================================================================
// Additional Requests Options (제조·용역 카테고리) - Manufacturing category
// ============================================================================

export const ADDITIONAL_REQUESTS_MANUFACTURING: EnumOption[] = [
  { value: 'sample_needed', label: '견본 필요' },
  { value: 'visit_meeting', label: '방문 미팅 요청' },
  { value: '3d_prototype', label: '3D 시제품 우선' },
  { value: 'technical_review', label: '기술 검토 요청' },
  { value: 'eco_friendly', label: '친환경 자재 사용' },
  { value: 'exact_deadline', label: '정확한 납기 필수' },
]

// ============================================================================
// Settlement Status Options (수수료 수납 상태)
// ============================================================================

/**
 * 정산 축이 "세모가 공급사에 보낼 대금" 에서 **"공급사에게서 받을 수수료"** 로 바뀌었다
 * (계약대금 직거래 전환). 값 집합은 그대로 두고 라벨만 옮긴다 — 값을 바꾸면 도메인 VO·
 * 백엔드 상수·화면이 동시에 깨지고 과거 행의 의미도 잃는다.
 *
 * - WAITING  확정 대기 (수수료가 아직 확정되지 않음)
 * - PENDING  미수 — 계약이 서서 청구됐으나 아직 받지 못함
 * - COMPLETED 수납 완료 — 업체에서 실제로 받음
 * - DEFERRED 보류 — 계약 취소 등으로 청구를 멈춤
 */
export const SETTLEMENT_STATUS_OPTIONS: EnumOption[] = [
  { value: 'WAITING', label: '확정대기' },
  { value: 'PENDING', label: '미수' },
  { value: 'COMPLETED', label: '수납완료' },
  { value: 'DEFERRED', label: '보류' },
]

export type SettlementStatus = 'WAITING' | 'PENDING' | 'COMPLETED' | 'DEFERRED'

// ============================================================================
// Contract Status Options (계약 상태)
// ============================================================================

export const CONTRACT_STATUS_OPTIONS: EnumOption[] = [
  { value: 'IN_PROGRESS', label: '진행 중' },
  { value: 'COMPLETED', label: '계약 완료' },
  { value: 'CANCELED', label: '계약 취소' },
]

export type ContractStatus = 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED'